import { colors } from '@lmc/common/constants'
import { Badge } from '@lmc/ui-kit'
import { NAMESPACE } from 'locale'
import { carePlansConstants } from 'locale/carePlans'
import type React from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'

export type SkillNeedOption = { id: string; name?: string }

/**
 * A need id as it actually arrives. The review form holds strings, but `ts-common` still
 * declares these as ObjectIds and the view screen passes the redux value straight through,
 * so normalising is the callers' job unless it is done here — and one of them will forget.
 */
export type NeedId = string | { toString: () => string }

/**
 * Legacy needs the plan's linked resident needs do not already cover.
 *
 * A legacy link and a resident need can point at the same Need template — adding the new one
 * never removed the old one — so the plan holds both and renders the need twice. Superseding
 * is per Need template, which is the only id the two models share.
 *
 * Used by the section that renders the tags and by the review form that clears the legacy
 * array on save. Both have to agree on what "covered" means, so neither should inline it.
 */
export const unsupersededSkillNeedIds = (
  skillNeedIds: NeedId[] | undefined,
  linkedNeeds: Array<{ need?: { id?: NeedId } | null }>
): string[] => {
  // Both sides through String, because a mismatch here fails silently and in the dangerous
  // direction: an id that never matches its own link looks unsuperseded, so the tag renders
  // twice — or, on the save path, the legacy array is never cleared.
  const superseded = new Set(
    linkedNeeds
      .map((linked) => linked.need?.id)
      .filter((id): id is NeedId => Boolean(id))
      .map(String)
  )

  return (skillNeedIds ?? []).map(String).filter((id) => !superseded.has(id))
}

interface LegacySkillNeedsProps {
  /** The care plan's legacy `skillNeeds` ids. */
  skillNeedIds?: NeedId[]
  /** Skill-need templates, used to resolve each id to a display name. */
  needOptions?: SkillNeedOption[]
}

/**
 * Needs linked to a care plan before MODs needs shipped live on the legacy
 * `skillNeeds` array rather than `residentNeeds`. Nothing migrates them, so
 * every Needs surface has to render them beside the new cards or they become
 * invisible once `mods_needs_care_plan` is on for the home.
 */
const LegacySkillNeeds: React.FC<LegacySkillNeedsProps> = ({
  skillNeedIds,
  needOptions
}) => {
  const { t } = useTranslation(NAMESPACE.CARE_PLANS)

  if (!skillNeedIds?.length) {
    return null
  }

  return (
    <BadgeRow data-testid="legacy-skill-needs">
      {skillNeedIds.map((skillNeedId) => (
        <Badge
          key={String(skillNeedId)}
          color={colors.grey}
          // Badge falls back to the literal "BADGE" on an empty text, so name
          // the gap explicitly: templates load asynchronously and a template
          // can be deleted out from under a plan that still links its need.
          text={
            needOptions?.find(
              (option) => String(option.id) === String(skillNeedId)
            )?.name || t(carePlansConstants.LEGACY_NEED_UNAVAILABLE)
          }
          textColor={colors.white}
          style={badgeStyle}
        />
      ))}
    </BadgeRow>
  )
}

const BadgeRow = styled.div`
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  margin-left: 15px;
  padding-top: 15px;
`

const badgeStyle = {
  marginBottom: 5,
  marginTop: 5,
  maxWidth: 'fit-content'
}

export default LegacySkillNeeds
